import { Link, useParams } from "react-router-dom";
import { LockKeyhole, Info } from "lucide-react";

import { normalizeReceivedList, normalizeReceived, dayOnly, dayTime, isPast } from "@/lib/v3Pass";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, PrivacyNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { ResultTable, ResultRow } from "../components/v3app";
import { ShareAside, PrivateBanner, initialOf, useExpired } from "../components/v3pass";

const MISSING = "Der gemeinsame Core kann noch keine an dich geteilten Angaben ausliefern (GET /member/access-pass/received fehlt). Es wird nichts angezeigt.";

// Recipient overview (M91 entry): only owner first name, purpose and expiry – values appear in the detail.
export function ReceivedList() {
  const res = useCore("/member/access-pass/received", (d) => ({ list: normalizeReceivedList(d) }));
  let body;
  if (res.error === "notfound") body = <StatusBanner tone="notice" icon={LockKeyhole} title="Noch nicht verfügbar" text={MISSING} testid="received-unavailable" />;
  else if (res.error) body = <ConnectionError onRetry={res.reload} />;
  else if (!res.data) body = <Loading label="Freigaben werden geladen …" />;
  else if (!res.data.list) body = <Unavailable testid="received-format">Die Antwort des Servers hat ein unerwartetes Format. Es wird nichts angezeigt.</Unavailable>;
  else if (!res.data.list.length) body = <p className="text-[14px] text-v3-muted" data-testid="received-empty">Niemand teilt gerade Angaben mit dir.</p>;
  else body = (
    <ResultTable label="Mit mir geteilte Angaben" testid="received-table">
      {res.data.list.map((s) => <ResultRow key={s.id} initial={initialOf(s.owner)} title={s.owner} to={`/access-pass/empfangen/${encodeURIComponent(s.id)}`} testid={`received-row-${s.id}`}
        context={`${s.purpose} · ${isPast(s.expiresAt) ? "abgelaufen" : s.expiresAt ? `bis ${dayOnly(s.expiresAt)}` : "Ende nicht angegeben"}`} />)}
    </ResultTable>
  );
  return (
    <div data-testid="received-list" data-m="M91">
      <V3Title title="Mit mir geteilte Angaben" intro="Hier siehst du Angaben, die andere ausdrücklich und zeitlich begrenzt mit dir teilen." testid="received-title" />
      {body}
      <div className="mt-[22px]"><PrivacyNote>Keine Bearbeitung fremder Angaben, kein öffentlich teilbarer Link.</PrivacyNote></div>
    </div>
  );
}

function Ended() {
  return (
    <div data-testid="received-ended" data-m="M92">
      <V3Title title="Freigabe wurde beendet" intro="Die beendete Freigabe zeigt keine früheren privaten Feldwerte mehr." testid="received-title" />
      <V3Columns layout="detail" aside={<ShareAside />} main={<>
        <StatusBanner tone="lilacLarge" icon={LockKeyhole} title="Freigabe beendet" text="Widerrufen oder abgelaufen" testid="received-ended-banner" />
        <DetailList>
          <DetailRow label="Freigabe" value="Beendet" testid="received-ended-state" />
          <DetailRow label="Weitere Angaben" value="Nicht mehr verfügbar" testid="received-ended-values" />
        </DetailList>
        <V3Actions>
          <Link to="/access-pass/empfangen" data-testid="received-to-overview" className={V3_PRIMARY}>Zur Freigabenübersicht</Link>
          <Link to="/access-pass" data-testid="received-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M91 / M92: only the released fields; after revocation (410) or expiry no earlier value is rendered.
function ReceivedView({ id }) {
  const res = useCore(`/member/access-pass/received/${encodeURIComponent(id)}`, (d) => ({ share: normalizeReceived(d) }));
  const expired = useExpired(res.data?.share?.expiresAt);
  if (res.error === "gone" || expired) return <Ended />;
  if (res.error === "notfound") return (<><V3Title title="Freigabe nicht verfügbar" testid="received-title" /><Unavailable testid="received-missing">Diese Freigabe gibt es nicht, sie ist nicht für dein Konto bestimmt oder der gemeinsame Core liefert sie noch nicht aus.</Unavailable></>);
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (!res.data) return <Loading label="Freigabe wird geladen …" />;
  const s = res.data.share;
  if (!s) return <Unavailable testid="received-detail-format">Die Antwort des Servers hat ein unerwartetes Format. Es wird nichts angezeigt.</Unavailable>;
  return (
    <div data-testid="received-detail" data-m="M91">
      <V3Title title="Mit dir geteilte Angaben" intro={`Du siehst ausschliesslich die Angaben, die ${s.owner} für diesen Zweck freigegeben hat.`} testid="received-title" />
      <V3Columns layout="detail" aside={<ShareAside />} main={<>
        <PrivateBanner title="Nur für dich freigegebene Angaben" text="Empfänger, Zweck, ausgewählte Angaben und Dauer gehören zu dieser einen Freigabe." testid="received-banner" />
        <DetailList>
          <DetailRow label="Geteilt von" value={s.owner} testid="received-owner" />
          <DetailRow label="Zweck" value={s.purpose} testid="received-purpose" />
          {s.fields.map((f) => <DetailRow key={f.key} label={f.label} value={f.value} testid={`received-field-${f.key}`} />)}
        </DetailList>
        {!s.fields.length && <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="received-no-fields">Diese Freigabe enthält keine lesbaren Angaben.</p>}
        <div className="mt-[11px]"><StatusBanner icon={Info} title="Zeitlich begrenzt" testid="received-limited"
          text={`Verfügbar bis ${dayTime(s.expiresAt)}. Nach Widerruf oder Ablauf werden frühere Werte nicht angezeigt.`} /></div>
        <V3Actions className="mt-[3px]">
          <Link to="/access-pass/empfangen" data-testid="received-to-overview" className={V3_PRIMARY}>Zur Freigabenübersicht</Link>
          <Link to="/access-pass" data-testid="received-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

export function ReceivedDetail() {
  const { id } = useParams();
  const { member } = useMemberAuth();
  return <ReceivedView key={`${member?.id || "anon"}:${id}`} id={id} />;
}
